// Write-clock freshness — the read side of the per-table write clock.
//
// THE INVARIANT
//
//   A cached entry may be served as fresh only if it can be PROVEN to have been
//   read at or after the current write clock of every table it actually touched.
//   Absent proof, it is stale.
//
// Freshness is never assumed; it is earned by a recorded post-read clock
// snapshot. This is what makes a stale read structurally impossible rather than
// merely unlikely — change-event invalidation alone is delivered a microtask (or
// a native bridge hop) after the commit, so it can always lose a race against a
// read that starts or completes in that window.
//
// Two distinct races, two mechanisms:
//
//   A. Commit BEFORE the read. The entry is `staleTime: Infinity`-fresh and its
//      invalidation hasn't flushed yet, so `fetchQuery` would serve the
//      pre-commit value without running the queryFn at all. Closed by
//      `isQueryBehindWriteClock`, wired into the root client's `staleTime`
//      function — it is evaluated synchronously at fetch time, so unlike
//      invalidation it cannot lose the race.
//
//   B. Commit DURING the read. The queryFn read pre-commit rows and its result
//      would come to rest in the cache marked fresh. Closed by
//      `withWriteClockReadGuard`, which re-runs the read when the clock advanced
//      while it was in flight.
//
// DEPENDENCY CAPTURE
//
// A query's table dependencies come from what it declared via
// `withTableChangeVersion` PLUS whatever nested `client.fetchQuery(...)` calls
// its queryFn made through its scoped client (tracked per read below). Composed
// queries are the classic source of hard-to-debug staleness: an outer query
// whose declared table list silently omits a table its inner read depends on
// looks correct until the omitted table changes. Capturing the union at run time
// removes that footgun — nobody has to keep a hand-written list complete.

import { type Query, type QueryClient, type QueryKey } from "@-/com.npmjs.tanstack__react-query";
import { watchAsyncStep } from "@-/dev.improve.haniverse.lib.async-watchdog";
import {
  addBufferedManualDatabaseChangeListener,
  hasTableWriteClockAdvanced,
  readTableWriteClockSnapshot,
} from "@-/com.npmjs.expo-sqlite";
import {
  type DatabaseQueryRegistration,
  getCachedDatabaseQueryRegistration,
  getDatabaseQueryRegistration,
} from "#table-change-version";
import { waitForDatabaseReadGates } from "#database-read-gate";
import { hasMinLength } from "@-/com.npmjs.ts-array-length";

// A read that keeps losing to concurrent commits must not spin forever. On
// exhaustion the guard records the PRE-read snapshot, so the entry stays marked
// behind the clock and the next fetch re-reads rather than trusting this one.
const MAX_READ_ATTEMPTS = 4;

// Freshness proof belongs to a query's cache lifetime. A weak key releases the snapshot with
// its registration and prevents identical hashes in separate clients sharing read proof.
let snapshotByRegistration = new WeakMap<DatabaseQueryRegistration, ReadonlyMap<string, number>>();

// Dependencies belong to actual cached Query objects, including untracked intermediary
// queries. Weak ownership prevents an evicted query's metadata retaining its data/client.
let dependenciesByQuery = new WeakMap<Pick<Query, "queryHash" | "queryKey">, ReadonlySet<string>>();
const sourceClients = new WeakMap<QueryClient, QueryClient>();

// The per-table write clock only advances while the buffered manual change feed
// has a subscriber — that feed bumps the clock on delivery, and it lazily attaches
// its upstream subscription on the first subscribe. Nothing else in the app
// subscribes, so without this the clock would sit at zero forever and every
// freshness check below would pass vacuously. Installed on first use and never
// removed: the clock must not miss a commit because no reader happened to be live.
let clockFeedSubscription: { remove(): void } | null = null;

function ensureWriteClockFeedAttached(): void {
  clockFeedSubscription ??= addBufferedManualDatabaseChangeListener(() => {
    // Bumping happens inside the feed itself; this subscriber exists only to keep
    // the feed attached.
  });
}

function addDependencies(target: Set<string>, tables: Iterable<string>): void {
  for (const tableName of tables) {
    target.add(tableName);
  }
}

function sourceQueryClient(client: QueryClient): QueryClient {
  return sourceClients.get(client) ?? client;
}

function isQueryReadMethod(
  key: PropertyKey,
): key is
  | "fetchQuery"
  | "ensureQueryData"
  | "prefetchQuery"
  | "fetchInfiniteQuery"
  | "ensureInfiniteQueryData"
  | "prefetchInfiniteQuery" {
  return (
    key === "fetchQuery" ||
    key === "ensureQueryData" ||
    key === "prefetchQuery" ||
    key === "fetchInfiniteQuery" ||
    key === "ensureInfiniteQueryData" ||
    key === "prefetchInfiniteQuery"
  );
}

/**
 * A query's own client records only reads issued by that query, even after an await.
 * A global stack across promises mistakes independent concurrent reads for children,
 * re-executes quiet reads and mixes their freshness dependencies. Recording the child
 * Query after its promise settles also covers cache hits and shared in-flight reads.
 */
function readScopedClient(client: QueryClient, dependencies: Set<string>): QueryClient {
  const source = sourceQueryClient(client);
  const methods = new Map<PropertyKey, unknown>();
  const scoped = new Proxy(source, {
    get(target, key) {
      if (methods.has(key)) {
        return methods.get(key);
      }
      if (isQueryReadMethod(key)) {
        const method = (options: Parameters<QueryClient["fetchQuery"]>[0]) => {
          const defaulted = source.defaultQueryOptions(options);
          // All six QueryClient read methods return a Promise; reflection preserves their
          // original receiver without forcing their different generic overloads into one type.
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- key is narrowed to the six Promise-returning QueryClient read methods
          const pending = Reflect.apply(source[key], source, [defaulted]) as Promise<unknown>;
          // QueryClient builds the Query synchronously, including when it returns cached data.
          // Hold that exact object; a removal/replacement during the read must not redirect
          // this read's dependency capture onto a different query lifetime.
          const query = source.getQueryCache().get(defaulted.queryHash);
          const registration =
            query === undefined ? undefined : getCachedDatabaseQueryRegistration(query);
          const collect = () => {
            if (registration !== undefined) {
              addDependencies(dependencies, registration.tables);
              addDependencies(dependencies, snapshotByRegistration.get(registration)?.keys() ?? []);
            }
            if (query !== undefined) {
              addDependencies(dependencies, dependenciesByQuery.get(query) ?? []);
            }
          };
          collect();
          return pending.finally(collect);
        };
        methods.set(key, method);
        return method;
      }
      const value: unknown = Reflect.get(target, key, target);
      // QueryClient uses native private fields; every other method needs the original receiver.
      if (typeof value === "function") {
        const bound: unknown = value.bind(target);
        methods.set(key, bound);
        return bound;
      }
      return value;
    },
  });
  sourceClients.set(scoped, source);
  return scoped;
}

function readScopedContext<TContext extends { client?: QueryClient }>(
  context: TContext,
  dependencies: Set<string>,
): TContext {
  if (context.client === undefined) {
    return context;
  }
  const client = readScopedClient(context.client, dependencies);
  // Do not spread QueryFunctionContext: reading its lazy signal getter marks the
  // fetch as abort-signal-consuming and changes TanStack's cancellation semantics.
  return new Proxy(context, {
    get(target, key) {
      return key === "client" ? client : Reflect.get(target, key, target);
    },
  });
}

/**
 * Wrap a queryFn so its result can never come to rest as fresh while behind the
 * write clock (race B), and so the freshness check (race A) has a snapshot to
 * compare against on later fetches.
 *
 * Applied at the query factories, so no call site opts in.
 */
// Typed over the context SHAPE rather than as a full `QueryFunction`: the guard
// reads only `queryKey` and forwards the context untouched, so it must stay
// callable by the leaner `queryFn({ queryKey })` form the actor builders use for
// their paired key getters, as well as by TanStack's full context.
export function withWriteClockReadGuard<
  TContext extends { client?: QueryClient; queryKey: QueryKey; signal?: AbortSignal },
  TData,
>(queryFn: (context: TContext) => TData | Promise<TData>): (context: TContext) => Promise<TData> {
  return async (context) => {
    ensureWriteClockFeedAttached();
    const client = context.client === undefined ? undefined : sourceQueryClient(context.client);
    const registration = getDatabaseQueryRegistration({ client, queryKey: context.queryKey });
    const query = client
      ?.getQueryCache()
      .get(client.defaultQueryOptions({ queryKey: context.queryKey }).queryHash);

    // Not a database-tracked query (a network read, or an immutable content /
    // static read that carries its own staleTime) — nothing to guard, but still
    // report upward so an enclosing guarded read isn't misled about its own deps.
    if (registration === undefined) {
      const dependencies = new Set<string>();
      const result = await queryFn(readScopedContext(context, dependencies));
      if (query !== undefined) {
        dependenciesByQuery.set(query, dependencies);
      }
      return result;
    }

    // Dependencies accumulate across attempts: tables discovered part-way through
    // attempt 1 have no "before" reading, so the retry re-runs with the complete
    // set known. This converges in two attempts in the normal case.
    const dependencies = new Set<string>(registration.tables);
    addDependencies(dependencies, snapshotByRegistration.get(registration)?.keys() ?? []);
    let lastResult: TData;

    for (let attempt = 1; ; attempt += 1) {
      const tables = [...dependencies];
      // Hold the read while an active gate covers any of its tables (the sign-in
      // deck bootstrap gates the actor tables until the initial pull settles) —
      // the query stays pending instead of resolving against rows that are about
      // to be replaced. Awaited BEFORE the clock snapshot so the pull's own
      // writes don't register as mid-read commits and burn retry attempts.
      // A held read gate is the second silent hang path: the read simply stays
      // pending, so it is not a slow query and never reaches any completion
      // handler. Report it while it is still blocked, naming the tables — that is
      // what identifies WHICH gate (today only the sign-in bootstrap installs one).
      // Bounded: every gate fails open at its max hold, and a cancelled query stops
      // waiting. The signal is read lazily (only while a gate blocks): reading TanStack's
      // `signal` getter changes its cancellation semantics for the whole fetch.
      await watchAsyncStep(`read gate: ${tables.slice(0, 4).join(",")}`, () =>
        waitForDatabaseReadGates(tables, () => context.signal),
      );
      const beforeSnapshot = readTableWriteClockSnapshot(tables);

      lastResult = await queryFn(readScopedContext(context, dependencies));
      if (query !== undefined) {
        dependenciesByQuery.set(query, new Set(dependencies));
      }
      const discoveredNewTables = !hasMinLength(tables, dependencies.size);

      // Settle only when the read spanned no commit to anything it touched AND
      // nothing new turned up that we hadn't clocked beforehand.
      if (!discoveredNewTables && !hasTableWriteClockAdvanced(beforeSnapshot)) {
        if (!registration.removed) {
          snapshotByRegistration.set(registration, beforeSnapshot);
        }
        return lastResult;
      }

      if (attempt >= MAX_READ_ATTEMPTS) {
        // Keep the stale marker: `beforeSnapshot` is behind the clock, so
        // `isQueryBehindWriteClock` stays true and the next fetch re-reads.
        if (!registration.removed) {
          snapshotByRegistration.set(registration, beforeSnapshot);
        }
        return lastResult;
      }
    }
  };
}

/**
 * True when `query` cannot be proven current — either it has no recorded
 * snapshot (never read through the guard) or one of the tables it depends on has
 * been written since. Drives the root client's `staleTime`, so an unprovable
 * entry is refetched rather than served.
 *
 * Fail-closed: for a database-tracked query, "don't know" means stale.
 */
export function isQueryBehindWriteClock(query: Pick<Query, "queryHash" | "queryKey">): boolean {
  ensureWriteClockFeedAttached();
  const registration = getCachedDatabaseQueryRegistration(query);
  if (registration === undefined) {
    // Untracked queries declared no database dependencies; time-based staleness
    // is not this mechanism's business.
    return false;
  }
  const snapshot = snapshotByRegistration.get(registration);
  if (snapshot === undefined) {
    return true;
  }
  return hasTableWriteClockAdvanced(snapshot);
}

/**
 * The `staleTime` for the root QueryClient: a database-tracked entry is fresh
 * only while it is provably current, and every other query keeps the
 * invalidation-driven `Infinity` default.
 */
export function resolveWriteClockStaleTime(query: Pick<Query, "queryHash" | "queryKey">): number {
  return isQueryBehindWriteClock(query) ? 0 : Number.POSITIVE_INFINITY;
}

/** Test seam: drop all recorded snapshots. */
export function clearWriteClockSnapshots(): void {
  snapshotByRegistration = new WeakMap();
  dependenciesByQuery = new WeakMap();
}
